import { useEffect, useRef, useState } from "react";
import api, { mediaUrl, formatError, uploadFileChunked } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Plus, Trash2, ListVideo, ChevronUp, ChevronDown, X, Clock, Pencil, Check, Zap, Upload, Loader2,
} from "lucide-react";

function QuickLoop({ media, onSave, onClose }) {
  const cats = ["All", ...Array.from(new Set(media.map((m) => m.category).filter(Boolean)))];
  const [cat, setCat] = useState("All");
  const [secs, setSecs] = useState(8);
  const pool = cat === "All" ? media : media.filter((m) => m.category === cat);

  const create = () => {
    if (!pool.length) return toast.error("No media in this folder yet");
    const name = (cat === "All" ? "All Media" : cat) + " Loop";
    const items = pool.map((m) => ({ media_id: m.id, duration: Number(secs) || 8, gap: 0 }));
    onSave({ name, items });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Instantly build a looping playlist from a whole folder — no picking one by one.</p>
      <div>
        <Label>Folder</Label>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="mt-1" data-testid="quick-loop-folder"><SelectValue /></SelectTrigger>
          <SelectContent>
            {cats.map((c) => (
              <SelectItem key={c} value={c} data-testid={`quick-folder-${c}`}>
                {c} ({c === "All" ? media.length : media.filter((m) => m.category === c).length})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Seconds per slide</Label>
        <Input type="number" min="2" value={secs} onChange={(e) => setSecs(e.target.value)} className="mt-1 w-28" data-testid="quick-loop-secs" />
      </div>
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5" /> {pool.length} items · ~{pool.length * (Number(secs) || 8)}s loop
      </p>
      <DialogFooter className="gap-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={create} data-testid="quick-loop-create"><Zap className="w-4 h-4 mr-1" /> Create loop</Button>
      </DialogFooter>
    </div>
  );
}

function Builder({ media, initial, onSave, onClose, reloadMedia }) {
  const [name, setName] = useState(initial?.name || "");
  const [items, setItems] = useState(initial?.items || []);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const onUploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (!files.length) return;
    setUploading(true);
    const newIds = [];
    for (const file of files) {
      try {
        const isVideo = (file.type || "").startsWith("video") || /\.(mp4|mov|webm|avi|mkv|m4v|qt)$/i.test(file.name);
        let doc;
        if (isVideo && file.size > 4 * 1024 * 1024) {
          doc = await uploadFileChunked(file);
        } else {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          doc = data;
        }
        if (doc?.id) newIds.push(doc.id);
      } catch (err) {
        toast.error(`${file.name}: ${formatError(err.response?.data?.detail) || "upload failed"}`);
      }
    }
    if (reloadMedia) await reloadMedia();
    if (newIds.length) {
      setItems((prev) => [
        ...prev,
        ...newIds.filter((id) => !prev.some((it) => it.media_id === id)).map((id) => ({ media_id: id, duration: 10, gap: 0 })),
      ]);
      toast.success(`${newIds.length} file(s) uploaded & added`);
    }
    setUploading(false);
  };

  const toggleItem = (mediaId) => setItems((prev) =>
    prev.some((it) => it.media_id === mediaId)
      ? prev.filter((it) => it.media_id !== mediaId)
      : [...prev, { media_id: mediaId, duration: 10, gap: 0 }]);
  const addAll = () => setItems(media.map((m) => ({ media_id: m.id, duration: 10, gap: 0 })));
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const setDur = (idx, d) => setItems(items.map((it, i) => (i === idx ? { ...it, duration: Number(d) } : it)));
  const setGap = (idx, g) => setItems(items.map((it, i) => (i === idx ? { ...it, gap: Number(g) } : it)));
  const isSelected = (id) => items.some((it) => it.media_id === id);
  const move = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= items.length) return;
    const copy = [...items];
    [copy[idx], copy[ni]] = [copy[ni], copy[idx]];
    setItems(copy);
  };

  const save = () => {
    if (!name.trim()) return toast.error("Name your playlist");
    if (!items.length) return toast.error("Add at least one item");
    onSave({ name, items });
  };

  const totalDur = items.reduce((a, b) => a + (b.duration || 0), 0);

  return (
    <div className="flex flex-col">
      <div className="grid md:grid-cols-2 gap-5 md:gap-6">
        {/* Sequence */}
        <div className="min-w-0">
          <Label>Playlist Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Weekend Offers" className="mb-4" data-testid="playlist-name-input" />
          <div className="flex items-center justify-between mb-2 gap-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Sequence ({items.length})</p>
            <span className="text-xs text-muted-foreground flex items-center gap-1 flex-shrink-0"><Clock className="w-3 h-3" /> {totalDur}s loop</span>
          </div>
          <p className="text-[11px] text-muted-foreground/70 mb-2"><b>show</b> = seconds visible · <b>gap</b> = black pause before next</p>
          <div className="space-y-2 md:max-h-[340px] md:overflow-y-auto md:pr-1">
            {items.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded">Add media from the picker below ↓</p>}
            {items.map((it, idx) => {
              const m = media.find((x) => x.id === it.media_id);
              return (
                <div key={idx} className="bg-secondary rounded-md p-2 space-y-2">
                  {/* Row 1: thumb + name + remove */}
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-8 bg-black rounded overflow-hidden flex-shrink-0">
                      {m && m.type === "image" && <img src={mediaUrl(m.id)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <span className="text-xs flex-1 truncate min-w-0">{m?.original_filename || "Missing"}</span>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0">#{idx + 1}</span>
                    <button onClick={() => removeItem(idx)} className="text-muted-foreground hover:text-destructive flex-shrink-0 p-1" data-testid={`item-remove-${idx}`}><X className="w-4 h-4" /></button>
                  </div>
                  {/* Row 2: timing + reorder */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] uppercase text-muted-foreground">show</span>
                      <Input type="number" min="1" value={it.duration} onChange={(e) => setDur(idx, e.target.value)} className="w-14 h-7 text-xs" data-testid={`item-duration-${idx}`} />
                      <span className="text-[9px] text-muted-foreground">s</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] uppercase text-muted-foreground">gap</span>
                      <Input type="number" min="0" value={it.gap ?? 0} onChange={(e) => setGap(idx, e.target.value)} className="w-14 h-7 text-xs" data-testid={`item-gap-${idx}`} />
                      <span className="text-[9px] text-muted-foreground">s</span>
                    </div>
                    <div className="flex items-center gap-1 ml-auto">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1.5 rounded bg-background text-muted-foreground hover:text-white disabled:opacity-30" data-testid={`item-up-${idx}`}><ChevronUp className="w-4 h-4" /></button>
                      <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="p-1.5 rounded bg-background text-muted-foreground hover:text-white disabled:opacity-30" data-testid={`item-down-${idx}`}><ChevronDown className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* Library picker */}
        <div className="min-w-0">
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={onUploadFiles} data-testid="builder-upload-input" />
          <div className="flex items-center justify-between mb-2 gap-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground truncate">Tap to add a photo/video</p>
            <div className="flex gap-1.5 flex-shrink-0">
              <Button type="button" size="sm" variant="default" className="h-7 text-xs px-2" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="builder-upload-btn">
                {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Upload
              </Button>
              <Button type="button" size="sm" variant="secondary" className="h-7 text-xs px-2" disabled={!media.length} onClick={addAll} data-testid="builder-add-all">
                Add all
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground/70 mb-2">Upload a new photo/video, or tap any item below to add it. Tap again to remove.</p>
          <div className="grid grid-cols-3 gap-2 md:max-h-[360px] md:overflow-y-auto md:pr-1">
            {media.map((m) => {
              const sel = isSelected(m.id);
              return (
                <button type="button" key={m.id} onClick={() => toggleItem(m.id)} data-testid={`builder-add-${m.id}`}
                  className={`group relative aspect-video bg-black rounded overflow-hidden border transition-colors ${sel ? "border-primary ring-2 ring-primary" : "border-border hover:border-muted-foreground"}`}>
                  {m.type === "image"
                    ? <img src={mediaUrl(m.id)} alt="" className="w-full h-full object-cover" />
                    : <video src={mediaUrl(m.id)} className="w-full h-full object-cover" muted preload="metadata" playsInline />}
                  <span className={`absolute bottom-1 right-1 w-6 h-6 rounded-full flex items-center justify-center ${sel ? "bg-primary" : "bg-green-600"}`}>
                    {sel ? <Check className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4 text-white" />}
                  </span>
                </button>
              );
            })}
            {media.length === 0 && (
              <div className="col-span-3 text-sm text-muted-foreground py-6 text-center">
                <p className="mb-2">No media yet.</p>
                <Button type="button" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="builder-upload-empty">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload photo/video
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Footer (outside grid so it never overlaps the picker) */}
      <div className="sticky bottom-0 z-20 -mx-6 px-6 mt-4 pt-3 pb-1 bg-background border-t border-border flex gap-2">
        <Button variant="secondary" className="flex-1 md:flex-none" onClick={onClose}>Cancel</Button>
        <Button className="flex-1 md:flex-none" onClick={save} data-testid="playlist-save">Save Playlist</Button>
      </div>
    </div>
  );
}

export default function Playlists() {
  const [playlists, setPlaylists] = useState([]);
  const [media, setMedia] = useState([]);
  const [open, setOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => Promise.all([api.get("/playlists"), api.get("/media")]).then(([p, m]) => {
    setPlaylists(p.data); setMedia(m.data);
  });
  const reloadMedia = () => api.get("/media").then((r) => { setMedia(r.data); return r.data; });
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    if (editing) await api.put(`/playlists/${editing.id}`, data);
    else await api.post("/playlists", data);
    toast.success("Playlist saved");
    setOpen(false); setEditing(null);
    load();
  };

  const quickSave = async (data) => {
    await api.post("/playlists", data);
    toast.success(`"${data.name}" created — assign it to a TV to start the loop`);
    setQuickOpen(false);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/playlists/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between gap-2 mb-5">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight">Playlists</h1>
          <p className="text-sm text-muted-foreground mt-0.5 truncate">Loop your slides on the TV — no AI, no cost</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
            <DialogTrigger asChild>
              <Button variant="secondary" data-testid="quick-loop-button"><Zap className="w-4 h-4 mr-1" /> Quick Loop</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md w-[calc(100%-1.5rem)]">
              <DialogHeader><DialogTitle className="text-lg pr-6 flex items-center gap-2"><Zap className="w-5 h-5 text-primary" /> Quick Loop</DialogTitle></DialogHeader>
              <QuickLoop media={media} onSave={quickSave} onClose={() => setQuickOpen(false)} />
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditing(null)} data-testid="new-playlist-button"><Plus className="w-4 h-4 mr-1" /> New Playlist</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-lg md:text-xl pr-6">{editing ? "Edit Playlist" : "Build Playlist"}</DialogTitle></DialogHeader>
              <Builder media={media} initial={editing} onSave={save} onClose={() => { setOpen(false); setEditing(null); }} reloadMedia={reloadMedia} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {playlists.length === 0 ? (
        <div className="border border-dashed border-border rounded-md py-20 text-center">
          <ListVideo className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">No playlists yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {playlists.map((p) => (
            <div key={p.id} data-testid={`playlist-${p.id}`} className="bg-card border border-border rounded-md p-4 fade-up">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display font-semibold">{p.name}</h3>
                  <p className="text-xs text-muted-foreground">{p.items?.length || 0} items · {p.items?.reduce((a, b) => a + (b.duration || 0), 0)}s</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditing(p); setOpen(true); }} data-testid={`playlist-edit-${p.id}`} className="p-1.5 text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(p.id)} data-testid={`playlist-delete-${p.id}`} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex gap-1.5 mt-3 overflow-hidden">
                {(p.items || []).slice(0, 5).map((it, i) => {
                  const m = media.find((x) => x.id === it.media_id);
                  return (
                    <div key={i} className="w-12 h-8 bg-black rounded overflow-hidden flex-shrink-0">
                      {m && m.type === "image" && <img src={mediaUrl(m.id)} alt="" className="w-full h-full object-cover" />}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
